<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonBadge,
  IonToggle,
  IonToolbar,
  modalController,
  onIonViewWillLeave,
} from "@ionic/vue";
import { useI18n } from "@/composables/useI18n";
import { LOCALES_OPTIONS } from "@/i18n";
import { useFabriqStore } from "@/store/fabriq";
import BackButton from "@/components/ui/BackButton.vue";
import { storeToRefs } from "pinia";
import UserAvatar from "@/components/business/UserAvatar.vue";
import AccountThemeModal from "@/components/modals/AccountThemeModal.vue";
import AccountLocaleModal from "@/components/modals/AccountLocaleModal.vue";
import AppVersion from "@/components/ui/AppVersion.vue";
import loader from "@/utils/loader";
import { useConfigStore } from "@/store/config";
import { useClientStore } from "@/store/client";
import DownloadLogsButton from "@/components/tools/DownloadLogsButton.vue";
import { reload } from "@/utils/live-updates";
import { lockOrientation, Orientation } from "@/utils/lockOrientation";

const clientStore = useClientStore();

const configStore = useConfigStore();
const fabriqStore = useFabriqStore();

const { config } = storeToRefs(configStore);
const { mobileScreenRotationEnabled } = storeToRefs(clientStore);
const {
  theme,
  locale,
  user,
  online,
  version,
  intercomEnabled,
  intercomUnreadCount,
} = storeToRefs(fabriqStore);
const router = useRouter();
const logout = async () => {
  await loader.show();
  await fabriqStore.clearPushNotifications();
  await fabriqStore.logout();
  router.replace("/login");
  await loader.hide();
};

const chooseTheme = async () => {
  const modal = await modalController.create({
    component: AccountThemeModal,
    canDismiss: true,
    mode: "ios",
    breakpoints: [0, 0.4, 0.6],
    initialBreakpoint: 0.4,
    componentProps: {
      onDone: async (action: string | undefined) => {
        await modal.dismiss();
        fabriqStore.setTheme(action);
      },
      onCancel: () => modal.dismiss(),
    },
  });
  return modal.present();
};
// TODO: when https://la.fabriq.tech/?ticket=2159195 is done, we'll have to rework
// the modal in this file to ensure we use the native scroll
// Also, we should add more breakpoints to this modal
const chooseLanguage = async () => {
  const modal = await modalController.create({
    component: AccountLocaleModal,
    canDismiss: true,
    mode: "ios",
    breakpoints: [0, 1],
    initialBreakpoint: 1,
    componentProps: {
      onDone: async (action: string) => {
        await modal.dismiss();
        fabriqStore.setLocale(action);
        clientStore.setTranslations(action);
      },
      onCancel: () => modal.dismiss(),
    },
  });

  return modal.present();
};

const hasPushNotifications = ref(config.value?.hasPushNotifications);

const changeHasPushNotifications = (value: boolean) => {
  if (value === hasPushNotifications.value) return;
  configStore.setHasNotifications(value);
  hasPushNotifications.value = value;
  configStore.saveUserConfig();
};

const orientation = ref(localStorage.getItem("orientation") || "portrait");

const changeOrientation = async () => {
  orientation.value = await lockOrientation(
    orientation.value === "portrait"
      ? Orientation.LANDSCAPE
      : Orientation.PORTRAIT
  );
};

const fullName = computed(() => {
  if (!user.value) return null;
  if (user.value?.profile?.full_name) return user.value?.profile?.full_name;
  return `${user.value.first_name
    .substring(0, 1)
    .toLocaleUpperCase()}${user.value.first_name
    .substring(1)
    .toLocaleLowerCase()}${user.value.last_name
    .substring(0, 1)
    .toLocaleUpperCase()}${user.value.last_name
    .substring(1)
    .toLocaleLowerCase()}`;
});

const selectedLocale = computed(() => {
  const found = LOCALES_OPTIONS.find((v) => v.value === locale.value);
  if (!found) return "";
  return found.label;
});

onIonViewWillLeave(() => {
  configStore.saveUserConfig();
});

const restartIconColor = computed(() => {
  return clientStore.client?.id === 1
    ? "var(--ion-color-primary-shade)"
    : "var(--ion-color-warning)";
});
const { t } = useI18n();
</script>

<template>
  <ion-page>
    <ion-header mode="ios">
      <ion-toolbar class="ion-padding-start transparent-toolbar">
        <back-button @back="$router.back()" slot="start" />
      </ion-toolbar>
    </ion-header>
    <ion-content :fullscreen="true" class="ticket-page normal" v-if="user">
      <user-avatar class="account-avatar" :user="user" />
      <div class="account-user-name">
        {{ fullName }}
      </div>
      <div class="account-user-email">
        {{ user.email }}
      </div>
      <div class="big-button" @click="chooseTheme">
        <font-icon
          name="lightbulb"
          material
          size="1"
          color="var(--ion-color-secondary)"
        />
        <div class="in-big-button">
          <div>{{ t("common.theme") }}</div>
          <div class="fake-dropdown">
            {{ t(`common.themes.${theme}`) }}
            <font-icon
              name="arrow_drop_down"
              color="var(--f-color-discrete)"
              material
              size="0.875"
            />
          </div>
        </div>
      </div>
      <div class="big-button" @click="chooseLanguage">
        <font-icon
          name="language"
          material
          size="1"
          color="var(--ion-color-secondary)"
        />
        <div class="in-big-button">
          <div>{{ t("common.locale") }}</div>
          <div class="fake-dropdown">
            {{ selectedLocale }}
            <font-icon
              name="arrow_drop_down"
              color="var(--f-color-discrete)"
              material
              size="0.875"
            />
          </div>
        </div>
      </div>
      <div class="big-button">
        <font-icon
          name="notifications"
          material
          outlined
          size="1"
          :color="
            online
              ? 'var(--ion-color-secondary)'
              : 'var(--ion-color-primary-shade)'
          "
        />
        <div class="in-big-button">
          <div>{{ t("common.push") }}</div>
          <ion-toggle
            class="toggle-btn"
            :checked="hasPushNotifications"
            @ionChange="changeHasPushNotifications(!hasPushNotifications)"
            color="secondary"
          />
        </div>
      </div>
      <div class="big-button" v-if="mobileScreenRotationEnabled">
        <font-icon
          name="screen_rotation"
          material
          outlined
          size="1"
          :color="
            online
              ? 'var(--ion-color-secondary)'
              : 'var(--ion-color-primary-shade)'
          "
        />
        <div class="in-big-button">
          <div>{{ t("common.landscapeMode") }}</div>
          <ion-toggle
            class="toggle-btn"
            :checked="orientation === 'landscape'"
            @ionChange="changeOrientation"
            color="secondary"
          />
        </div>
      </div>
      <div class="big-button logout clickable" @click="logout">
        <font-icon
          name="logout"
          material
          size="1"
          color="var(--ion-color-danger)"
        />
        <div>{{ t("common.logout") }}</div>
      </div>
      <div
        v-if="intercomEnabled"
        class="big-button intercom"
        @click="fabriqStore.openIntercomChat"
      >
        <font-icon
          name="intercom"
          brand
          size="1"
          color="var(--ion-color-secondary)"
        />
        <div class="in-big-button">
          <div>{{ t("common.getSupport") }}</div>
          <ion-badge class="intercom-badge" color="secondary">
            {{ intercomUnreadCount }}
          </ion-badge>
        </div>
      </div>
      <div class="big-button restart clickable" @click="reload">
        <font-icon name="refresh" material size="1" :color="restartIconColor" />
        <div>{{ t("common.restart") }}</div>
      </div>
      <download-logs-button class="account-download-btn" big />
      <app-version :version="version" position="block" />
    </ion-content>
  </ion-page>
</template>

<style scoped>
.in-big-button ion-toggle {
  --track-background-checked: var(--ion-color-secondary);
  --track-background: var(--ion-color-primary-shade);
  width: 40px;
  height: 20px;
  --handle-width: 16px;
  --handle-height: 16px;
}

.big-button {
  display: flex;
  flex-direction: row;
  gap: var(--ion-padding);
  align-items: center;
  justify-content: flex-start;
  margin: var(--ion-padding);
  padding: calc(var(--ion-padding) * 2) calc(var(--ion-padding) * 2);
  padding-right: var(--ion-padding);
  background-color: var(--ion-color-primary-contrast);
  color: var(--ion-color-primary);
  font-size: var(--font-size-m);
  border: 1px solid var(--ion-border-color);
  border-radius: var(--f-border-radius);
  flex-wrap: wrap;
}

.in-big-button {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.fake-dropdown {
  display: flex;
  flex-direction: row;
  gap: var(--ion-padding);
  align-items: center;
  justify-content: space-end;
  color: var(--ion-color-primary-shade);
}

.logout {
  margin-bottom: calc(var(--ion-padding) * 4);
}
.restart {
  margin-bottom: calc(var(--ion-padding) * 4);
}

.account-avatar.user-avatar {
  --avatar-size: 5rem;
  justify-content: center;
}

.account-user-name {
  text-align: center;
  margin-top: var(--ion-padding);
  color: var(--ion-color-primary);
  font-size: var(--font-size-xl);
  font-weight: bold;
}

.account-user-email {
  text-align: center;
  color: var(--ion-color-primary-shade);
  font-size: var(--font-size-m);
  margin-bottom: calc(var(--ion-padding) * 4);
}

.with-background {
  background-color: var(--ion-background-color);
}

.toggle-btn {
  padding: 0;
}
.account-download-btn {
  bottom: -32px;
}

.custom-alert .alert-wrapper {
  background: var(--ion-background-color);
}
</style>
